/**
 * The calls screens 09 and 10 make, in the shape of `src/api.ts`: bearer
 * token on every request, a typed error so a 403 on approve reads as "your
 * role does not allow that" rather than "something went wrong".
 *
 * Both screens live in one client because they read each other's data — the
 * order screen needs the supplier list to pick from, and the supplier screen's
 * "start an order" hands straight over — and splitting them would mean two
 * copies of the same request wrapper.
 */

import type {
  OrderLine,
  OrderReview,
  OrderStatus,
  OrderType,
  PackUnit,
  TopUpSuggestion,
} from "@baros/core";
import { request as shared } from "../api.js";

async function request<T>(
  token: string,
  method: "GET" | "PUT" | "POST" | "DELETE",
  path: string,
  body?: unknown,
): Promise<T> {
  // The shared client: it refreshes an expired token once and ends
  // the session when it cannot (see `request` in src/api.ts).
  return shared<T>(token, method, path, body);
}

const v = (venueId: string) => encodeURIComponent(venueId);

// ---------------------------------------------------------------- suppliers

/** One line a supplier carries — a `SUPITEM#` row as the screen sees it. */
export interface SupplierItem {
  readonly supplierId: string;
  readonly itemId: string;
  /** Null for anything with no catalogue entry: limes, block ice. */
  readonly ingredientId: string | null;
  readonly name: string;
  readonly pack: string;
  readonly unitCostCents: number;
  readonly par: number;
  readonly packSize: number;
  readonly unit: PackUnit;
  readonly unitMl?: number;
}

export interface Supplier {
  readonly supplierId: string;
  readonly name: string;
  readonly category?: string;
  readonly rep?: string;
  readonly phone?: string;
  readonly email?: string;
  readonly channel?: "email" | "ordermentum" | "phone";
  readonly terms?: string;
  /** As shown: `Tue, Fri · before 11:00`. */
  readonly cutoff?: string;
  readonly cutoffWeekdays?: readonly string[];
  readonly cutoffTime?: string;
  readonly deliveryDays?: readonly string[];
  readonly minimumExGstCents?: number;
  readonly leadDays?: number;
  readonly abn?: string;
  readonly account?: string;
  readonly items: readonly SupplierItem[];
}

// ------------------------------------------------------------------- orders

export interface Order {
  readonly orderId: string;
  readonly supplierId: string;
  readonly supplierName?: string;
  readonly type: OrderType;
  readonly lines: readonly OrderLine[];
  readonly subtotalExGstCents: number;
  readonly subtotalIncGstCents: number;
  readonly status: OrderStatus;
  readonly createdBy: string;
  readonly createdAt: string;
  readonly submittedAt?: string;
  readonly approvedBy?: string;
  readonly approvedAt?: string;
  readonly sentAt?: string;
  readonly cancelledBy?: string;
  readonly cancelledAt?: string;
}

export interface OrderList {
  readonly orders: readonly Order[];
  readonly limits: { bartender: number; manager: number; owner: number };
  /** Which tier the signed-in person acts at here, or null if they have none. */
  readonly limitRole: "bartender" | "manager" | "owner" | null;
  readonly limitCents: number | null;
  readonly orderTypes: readonly OrderType[];
}

export interface Suggestion {
  readonly supplierId: string;
  readonly supplierName: string;
  readonly type: OrderType;
  readonly lines: readonly TopUpSuggestion[];
  readonly review: OrderReview;
}

export const ordersApi = {
  suppliers: (token: string) =>
    request<{ suppliers: Supplier[] }>(token, "GET", "/v1/suppliers"),

  /**
   * The venue's stock levels — screen 05's endpoint, read here because a par
   * without a holding beside it is not a shortfall.
   *
   * Read rather than joined server-side: suppliers are org-scoped and stock is
   * venue-scoped, and putting one venue's holdings on an org list would be the
   * wrong shape the moment a second venue exists.
   */
  stock: (token: string, venueId: string) =>
    request<{ stock: { ingredientId: string; heldMl: number }[] }>(
      token,
      "GET",
      `/v1/venues/${v(venueId)}/stock`,
    ),

  /** Create a supplier; the id is derived from the name, so a repeat is a 409. */
  createSupplier: (token: string, body: unknown) =>
    request<{ supplier: Supplier }>(token, "POST", "/v1/suppliers", body),

  /** Partial update: only the fields sent change. */
  saveSupplier: (token: string, supplierId: string, body: unknown) =>
    request<{ supplier: Supplier }>(
      token,
      "PUT",
      `/v1/suppliers/${encodeURIComponent(supplierId)}`,
      body,
    ),

  /** Remove a supplier and every line it carries. */
  deleteSupplier: (token: string, supplierId: string) =>
    request<void>(token, "DELETE", `/v1/suppliers/${encodeURIComponent(supplierId)}`),

  deleteItem: (token: string, supplierId: string, itemId: string) =>
    request<void>(
      token,
      "DELETE",
      `/v1/suppliers/${encodeURIComponent(supplierId)}/items/${encodeURIComponent(itemId)}`,
    ),

  saveItem: (token: string, supplierId: string, itemId: string, body: unknown) =>
    request<{ item: SupplierItem }>(
      token,
      "PUT",
      `/v1/suppliers/${encodeURIComponent(supplierId)}/items/${encodeURIComponent(itemId)}`,
      body,
    ),

  orders: (token: string, venueId: string) =>
    request<OrderList>(token, "GET", `/v1/venues/${v(venueId)}/orders`),

  suggest: (token: string, venueId: string, supplierId: string, type: OrderType) =>
    request<Suggestion>(
      token,
      "GET",
      `/v1/venues/${v(venueId)}/orders/suggest?supplierId=${encodeURIComponent(supplierId)}` +
        `&type=${encodeURIComponent(type)}`,
    ),

  createOrder: (
    token: string,
    venueId: string,
    body: { supplierId: string; type: OrderType; lines: readonly OrderLine[] },
  ) =>
    request<{ order: Order; review: OrderReview }>(
      token,
      "POST",
      `/v1/venues/${v(venueId)}/orders`,
      body,
    ),

  updateOrder: (
    token: string,
    venueId: string,
    orderId: string,
    body: { lines: readonly OrderLine[]; type?: OrderType },
  ) =>
    request<{ order: Order; review: OrderReview }>(
      token,
      "PUT",
      `/v1/venues/${v(venueId)}/orders/${encodeURIComponent(orderId)}`,
      body,
    ),

  submitOrder: (token: string, venueId: string, orderId: string) =>
    request<{ order: Order; review: OrderReview }>(
      token,
      "POST",
      `/v1/venues/${v(venueId)}/orders/${encodeURIComponent(orderId)}/submit`,
    ),

  approveOrder: (token: string, venueId: string, orderId: string) =>
    request<{ order: Order; review: OrderReview }>(
      token,
      "POST",
      `/v1/venues/${v(venueId)}/orders/${encodeURIComponent(orderId)}/approve`,
    ),

  cancelOrder: (token: string, venueId: string, orderId: string) =>
    request<{ order: Order }>(
      token,
      "POST",
      `/v1/venues/${v(venueId)}/orders/${encodeURIComponent(orderId)}/cancel`,
    ),
};
